/**
 * The source dictionary.
 *
 * Flat keys on purpose: `ru.ts` is typed against this object, so a forgotten
 * translation is a build error rather than something a user finds. A nested
 * shape would type-check the same way but report the miss as a mismatch on the
 * whole tree, which points at nothing in particular.
 *
 * Entries with plural forms are objects; `translate` picks the form with
 * `Intl.PluralRules` from the parameter named `n`.
 */
export const en = {
  'nav.today': 'Today',
  'nav.history': 'History',
  'nav.calendar': 'Calendar',
  'nav.timelapse': 'Timelapse',
  'nav.settings': 'Settings',

  'common.loading': 'Loading…',
  'common.sign_out': 'Sign out',
  'common.cancel': 'Cancel',
  'common.api_unreachable': 'Could not reach the API: {message}',

  'capture.today': 'Today',
  'capture.streak': {
    one: '{n} day streak',
    other: '{n} day streak',
  },
  'capture.checking_today': 'Checking today…',
  'capture.opening_camera': 'Opening the camera…',
  'capture.shutter': 'Take today’s photo',
  'capture.uploading': 'Uploading…',
  'capture.gallery': 'Choose from gallery instead',
  'capture.photo_alt': 'Today’s selfie',
  'capture.upload_failed': 'That upload did not go through.',
  'capture.processing': 'Processing…',
  'capture.done': 'Done for today. See you tomorrow.',
  'capture.status': 'Status: {status}',
  // The API's own enum, spelled out: interpolating the raw value would leave an
  // English word inside an otherwise translated sentence.
  'capture.status_pending': 'pending',
  'capture.status_processing': 'processing',
  'capture.status_ready': 'ready',
  'capture.status_failed': 'failed',
  'capture.hint_unavailable':
    'Face alignment is off — your photo will be saved as framed.',
  'capture.hint_loading': 'Loading face alignment…',
  'capture.hint_outline': 'Line your face up with the outline.',
  'capture.hint_searching': 'Looking for your face…',
  'capture.hint_closer': 'Move closer and hold your head level.',

  'history.title': 'History',
  'history.count': {
    one: '{n} day so far',
    other: '{n} days so far',
  },
  'history.subtitle_empty': 'Every day you showed up.',
  'history.empty': 'Nothing here yet. Your first photo starts the timelapse.',
  'history.load_failed': 'Could not load your history: {message}',
  'history.loading_more': 'Loading more…',

  'calendar.subtitle': 'Habit calendar',
  'calendar.filled': {
    one: '{filled} of {n} day',
    other: '{filled} of {n} days',
  },
  'calendar.previous_month': 'Previous month',
  'calendar.next_month': 'Next month',
  'calendar.load_failed': 'Could not load that month: {message}',
  'calendar.mon': 'Mon',
  'calendar.tue': 'Tue',
  'calendar.wed': 'Wed',
  'calendar.thu': 'Thu',
  'calendar.fri': 'Fri',
  'calendar.sat': 'Sat',
  'calendar.sun': 'Sun',

  'timelapse.title': 'Timelapse',
  'timelapse.subtitle': 'Watch the year go past.',
  'timelapse.range': 'Range',
  'timelapse.range_week': 'Week',
  'timelapse.range_month': 'Month',
  'timelapse.range_year': 'Year',
  'timelapse.range_all': 'All time',
  'timelapse.render': 'Render',
  'timelapse.rendering': 'Rendering…',
  'timelapse.progress_label': 'Render progress',
  'timelapse.frames': {
    one: '{done} of {n} frame',
    other: '{done} of {n} frames',
  },
  'timelapse.on_server':
    'This browser cannot encode video, so the server is rendering it…',
  'timelapse.download': 'Download',
  'timelapse.local': 'Rendered on your device — it never left it.',
  'timelapse.render_failed': 'The render failed.',
  'timelapse.start_failed': 'Could not start the render.',

  'settings.title': 'Settings',
  'settings.remind_at': 'Remind me at',
  'settings.update_timezone': 'update from {zone}',
  'settings.how': 'How',
  'settings.channel_both': 'Push and email',
  'settings.channel_both_hint': 'Whichever reaches you first.',
  'settings.channel_push': 'Push only',
  'settings.channel_push_hint': 'Needs an installed app on iOS.',
  'settings.channel_email': 'Email only',
  'settings.channel_email_hint': 'Works everywhere.',
  'settings.channel_off': 'Off',
  'settings.channel_off_hint': 'No reminders at all.',
  'settings.language': 'Language',
  'settings.language_hint': 'Also the language of your reminder emails.',
  'settings.this_device': 'This device',
  'settings.push_enable': 'Enable push on this device',
  'settings.push_disable': 'Stop push on this device',
  'settings.push_denied':
    'Notifications are blocked in your browser settings for this site. Email reminders still work.',
  'settings.push_unsupported':
    'This browser cannot receive push notifications. On iPhone, add Selflapse to your home screen first. Email reminders work regardless.',

  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.name': 'Name',
  'auth.confirm_password': 'Confirm password',
  'auth.login_title': 'Welcome back',
  'auth.login_subtitle': 'One selfie a day.',
  'auth.sign_in': 'Sign in',
  'auth.signing_in': 'Signing in…',
  'auth.create_account_link': 'Create an account',
  'auth.forgot_link': 'Forgot password',
  'auth.register_title': 'Start your timelapse',
  'auth.register_subtitle': 'Your calendar day is read in {zone}.',
  'auth.have_account': 'I already have an account',
  'auth.consent': 'I agree to Selflapse storing and processing photographs of my face.',
  'auth.create_account': 'Create account',
  'auth.creating_account': 'Creating your account…',
  'auth.forgot_title': 'Reset your password',
  'auth.forgot_subtitle': 'We will email you a link.',
  'auth.back_to_sign_in': 'Back to sign in',
  'auth.send_link': 'Send reset link',
  'auth.sending': 'Sending…',
  'auth.reset_title': 'Choose a new password',
  'auth.new_password': 'New password',
  'auth.save_password': 'Save password',
  'auth.saving': 'Saving…',
  'auth.reset_done': 'You can sign in now.',
  'auth.link_incomplete_title': 'That link is incomplete',
  'auth.link_incomplete': 'The reset link is missing its token. Request a new one.',
  'auth.request_new_link': 'Request a new link',
  'auth.check_email':
    'Your account is created. Open the link we sent to {email} to finish, then sign in.',
  'auth.check_email_hint':
    'Nothing there? The link can take a minute, and spam filters are wary of new senders.',
  'auth.verified_ok': 'Your address is confirmed. You can sign in now.',
  'auth.verified_already': 'That address was already confirmed. You can sign in.',
  'auth.verified_invalid':
    'That confirmation link is no longer valid. Sign in and we will send a new one.',
  'auth.resend_verification': 'Send a new confirmation link',

  // Read by the service worker, which cannot hold a hook. Only ever shown for a
  // push that arrives with no body; the server sends translated text otherwise,
  // and `lang/{en,ru}/app.php` must keep saying the same thing as these two.
  'push.title': 'Time for today’s selfie',
  'push.body': 'One photo keeps the streak alive.',

  'health.subtitle': 'Local environment status',
  'health.checking': 'Checking services…',
  'health.unreachable': 'API unreachable: {message}',
  'health.ok': 'All systems go',
  'health.degraded': 'Degraded',

  // Thrown from modules that are not components and cannot hold a hook, so the
  // key travels inside the Error and the screen translates it on render.
  'errors.camera_unsupported': 'This browser cannot open the camera.',
  'errors.camera_denied':
    'Camera access was blocked. You can still upload from your gallery.',
  'errors.camera_unavailable': 'No camera is available on this device.',
  'errors.camera_not_ready': 'The camera preview is not ready yet.',
  'errors.push_unsupported': 'This browser cannot receive push notifications.',
  'errors.push_blocked':
    'Notifications are blocked for this site. You can still get email reminders.',
  'errors.push_not_configured': 'Push is not configured on the server yet.',
  'errors.push_failed': 'Push could not be changed on this device.',
  'errors.save_failed': 'That setting could not be saved.',
  'errors.canvas_context': 'Could not acquire a 2D drawing context.',
  'errors.encode_failed': 'The browser failed to encode the captured frame.',
  'errors.no_frame': 'The camera has not produced a frame yet.',
  'errors.upload_failed': 'The photo could not be uploaded to storage.',
  'errors.photo_not_ready': 'The photo from {date} is not ready yet.',
  'errors.photo_load_failed': 'Could not load the photo from {date}.',
  'errors.range_empty': 'There are no photos in that range yet.',
  'errors.encoder_empty': 'The encoder produced no output.',
  'errors.request_failed': 'Request to {path} failed',
} as const;
